import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useScrollToTop} from '../hooks/useScrollToTop';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiArrowRight,FiClock,FiActivity,FiCpu,FiSmile,FiCheckCircle,FiBarChart2,FiPlay,FiMail}=FiIcons;

export default function LandingPage() {
  // Use the hook to handle scrolling on mount/navigation
  useScrollToTop();
  
  const fadeInUp={initial: {opacity: 0,y: 20},whileInView: {opacity: 1,y: 0},viewport: {once: true},transition: {duration: 0.6}};
  const staggerContainer={hidden: {opacity: 0},show: {opacity: 1,transition: {staggerChildren: 0.2}}};

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed w-full z-50 top-0 bg-white/80 backdrop-blur-md border-b border-green-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-green-600 rounded-lg flex items-center justify-center text-white">
                <SafeIcon icon={FiActivity} className="text-xl" />
              </div>
              <span className="font-bold text-xl tracking-tight text-emerald-900">Mindul Flow</span>
            </div>
            <div className="flex items-center gap-4">
              <motion.div whileHover={{scale: 1.05}} whileTap={{scale: 0.95}}>
                <Link 
                  to="/contact"
                  className="text-emerald-600 hover:text-emerald-700 font-medium text-sm transition-colors"
                >
                  Contact
                </Link>
              </motion.div>
              <motion.a whileHover={{scale: 1.05}} whileTap={{scale: 0.95}} href="https://demo.mindulflow.com/" target="_blank" rel="noopener noreferrer" className="bg-emerald-600 text-white px-5 py-2 rounded-full font-medium text-sm hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-200">
                Launch App
              </motion.a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-gradient-to-b from-green-50 to-white">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top-right,_var(--tw-gradient-stops))] from-green-200/40 via-transparent to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom-left,_var(--tw-gradient-stops))] from-emerald-200/40 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{duration: 0.6}}>
              <span className="inline-block py-1 px-3 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold mb-6 border border-emerald-200">✨ AI-Powered Productivity</span>
              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 mb-8 leading-tight">
                Find Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500">Natural Rhythm</span>
              </h1>
              <p className="text-xl text-slate-600 mb-10 leading-relaxed max-w-2xl mx-auto">
                Mindul Flow isn't just a timer. It's an intelligent companion that uses AI to analyze your focus patterns, helping you achieve peak productivity without the burnout.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <motion.a whileHover={{scale: 1.05}} whileTap={{scale: 0.95}} href="https://demo.mindulflow.com/" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-8 py-4 bg-emerald-600 text-white rounded-2xl font-bold text-lg hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-200 flex items-center justify-center gap-2">
                  Start Flowing Free <SafeIcon icon={FiArrowRight} />
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Why Choose Mindul Flow?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We combine the proven Pomodoro technique with modern AI to create a personalized productivity experience.
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-3 gap-8" variants={staggerContainer} initial="hidden" whileInView="show" viewport={{once: true}}>
            {[
              {icon: FiCpu,title: "AI Classification",desc: "Our advanced AI analyzes your tasks and automatically categorizes your time usage patterns for deeper insights."},
              {icon: FiBarChart2,title: "Smart Analytics",desc: "Visualize your productivity with beautiful charts. Understand exactly where your time goes and how to optimize it."},
              {icon: FiSmile,title: "Mindful Breaks",desc: "Smart rest intervals prevent burnout. We remind you to take breaks that actually recharge your mental energy."}
            ].map((feature,index)=> (
              <motion.div key={index} variants={{hidden: {opacity: 0,y: 20},show: {opacity: 1,y: 0}}} className="bg-green-50 rounded-3xl p-8 border border-green-100 hover:shadow-xl hover:shadow-green-100/50 transition-all duration-300">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-emerald-600 text-2xl shadow-sm mb-6">
                  <SafeIcon icon={feature.icon} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-white to-green-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-[3rem] p-10 md:p-16 text-center text-white relative overflow-hidden shadow-2xl shadow-emerald-200">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
            <motion.div initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} viewport={{once: true}} className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to optimize your day?</h2>
              <p className="text-emerald-100 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
                Join thousands of mindful achievers who are reclaiming their time with Mindul Flow. Free forever.
              </p>
              <motion.a whileHover={{scale: 1.05}} whileTap={{scale: 0.95}} href="https://demo.mindulflow.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-white text-emerald-700 px-10 py-5 rounded-2xl font-bold text-xl hover:bg-emerald-50 transition-all shadow-lg">
                Launch Mindul Flow <SafeIcon icon={FiArrowRight} />
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiActivity} className="text-emerald-600 text-xl" />
            <span className="font-bold text-lg text-slate-900">Mindul Flow</span>
          </div>
          <div className="text-slate-500 text-sm">
            &copy;{new Date().getFullYear()} Mindul Flow. All rights reserved.
          </div>
          <div className="flex gap-6">
            <Link 
              to="/contact"
              className="text-slate-400 hover:text-emerald-600 transition-colors"
            >
              Contact
            </Link>
            <a href="#" className="text-slate-400 hover:text-emerald-600 transition-colors">Privacy</a>
            <a href="#" className="text-slate-400 hover:text-emerald-600 transition-colors">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}